import { GenerationType, PhotoStudioFlowType } from 'constants/visualStaging/visualStagingTypes'
import { MaskDrawerProvider, useMaskDrawer } from '../MaskDrawer/MaskDrawer.context'
import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { AIFeatureType } from '../../../common/useAIProgressMessages'
import { AiLabelOverlay } from '../AiLabel'
import { BeforeAfterSlider } from 'components/common/BeforeAfterSlider'
import { ExpandedVisualType } from 'components/pages/Gallery/_main/ClientGallery/ClientGalleryPhoto'
import { GRAY_1100 } from 'constants/styling/theme'
import { MaskDrawer } from '../MaskDrawer/MaskDrawer.controller'
import { PhotoStudioGenerating } from '../../../common/PhotoStudioLoading/PhotoStudioGenerating.component'
import { LabLiveOverlay } from '../lab/LabLiveOverlay.component'
import { LabRevealOverlay } from '../lab/LabRevealOverlay.component'
import { LabRunBar } from '../lab/LabRunBar.component'
import { preloadImage } from '../lab/preloadImage'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { PhotoStudioPreviewToolbar } from '../PhotoStudioPreviewToolbar'
import { PhotoStudioSaving } from '../../../common/PhotoStudioSaving/PhotoStudioSaving.component'
import { PhotoStudioSidebarOption } from '../ClientPhotoStudio.context'
import { PreviewContainer } from '../PreviewContainer/PreviewContainer.component'
import { ProcessingMode } from 'constants/visualStaging/visualStagingTypes'
import { UndoRedoPanel as SharedUndoRedoPanel } from 'components/pages/PhotoStudioPhotoEditing/common/UndoRedoPanel'
import { Stack } from '@mui/material'
import { VirtualStagingState } from 'models/visualStaging/virtualStagingTypes'
import { VisualTypeSwitcher } from 'components/pages/PhotoStudioPhotoEditing/common/VisualTypeSwitcher'
import enhanceDemoAfter from 'assets/img/ai_settings/enhance_demo_after.webp'
import enhanceDemoBefore from 'assets/img/ai_settings/enhance_demo_before.webp'
import { logger } from 'utils/logging'
import { useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { useTranslation } from 'react-i18next'

/**
 * Maps GenerationType to AIFeatureType for dynamic progress messages
 */
const getAIFeatureType = (generationType?: GenerationType, isMagicEraser?: boolean, isEnhancement?: boolean): AIFeatureType => {
  if (isMagicEraser) return AIFeatureType.MAGIC_ERASER
  if (isEnhancement) return AIFeatureType.ENHANCEMENT

  switch (generationType) {
    case GenerationType.DECLUTTER_ROOM:
      return AIFeatureType.DECLUTTER_ROOM
    case GenerationType.PAINT_WALLS:
      return AIFeatureType.PAINT_WALLS
    case GenerationType.CHANGE_FLOOR:
      return AIFeatureType.FLOOR_REPLACEMENT
    default:
      return AIFeatureType.VIRTUAL_STAGING
  }
}

export interface UnifiedPreviewProps {
  // Core image and subscription props
  /** Original image URL for the visual being edited */
  imageUrl: string
  /** Current preview image URL (may be modified during editing) */
  previewImage: string
  /** Whether the user has an active subscription (affects UI interactions) */
  hasWorkspaceActiveSubscription: boolean
  /** Whether an AI generation is currently in progress */
  isProcessingImage: boolean
  /** Whether the result is currently being saved */
  isSaving: boolean
  /** Progress information for AI generation steps */
  generationProgress?: any

  // Flow configuration props
  /** Current PhotoStudio flow type (determines which UI components to show) */
  currentFlow: PhotoStudioFlowType
  /** Selected Pintura manual tool (e.g., CROP, FILTER) */
  selectedPinturaFeature?: string

  // Visual Staging specific props
  /** Visual Staging state including room type, style, processing mode, etc. */
  virtualStagingState?: VirtualStagingState
  /** Callback when Visual Staging style is changed */
  onVisualStagingStyleChange?: (style: any) => void

  // Magic Eraser specific props
  /** Whether Magic Eraser tool is currently selected */
  isMagicEraserTool: boolean
  /** Whether AI eraser generation has completed */
  hasAIEraserCompleted: boolean
  /** Trigger value to clear mask (incremented when mask should be cleared) */
  clearMaskTrigger: number
  /** Callback when mask is drawn or cleared */
  onMaskChange?: () => void
  /** Callback when preview image changes (e.g., from undo/redo) */
  onPreviewImageChange?: (imageUrl: string) => void

  // Generation props
  /** Callback to trigger AI generation */
  onGenerate: (maskedImageFile?: File) => void
  /** Ref to the generate function for Magic Eraser */
  generateRef: React.RefObject<{ handleMagicEraserGenerate: () => Promise<void> }>

  // Analytics props
  /** Analytics logging function */
  logPhotoStudioEvent?: (eventName: string, params?: {}) => void
  /** Assignment ID for analytics */
  assignmentId?: string
  /** Source visual filename for analytics */
  sourceVisualFilename?: string
}

export interface UnifiedPreviewRef {
  /** Get the current PhotoStudio flow type */
  getCurrentFlow: () => PhotoStudioFlowType
  /** Get the current preview image URL */
  getPreviewImage: () => string
}

/**
 * Unified preview component that handles all PhotoStudio flows:
 */
interface MaskDrawerWrapperProps {
  onGenerate: (maskedImageFile?: File) => void
  isLabSdkEngine: boolean
  isDisabled: boolean
}

/**
 * MaskDrawer wrapper for Magic Eraser and Visual Staging masking: exposes Generate (mask export) to the controller and
 * mirrors mask state into the context refs. Defined at module level on purpose — a component declared inside
 * UnifiedPreview's render is a new type on every render, so React remounted the drawer each time: a blank canvas, then
 * the mask restored asynchronously, i.e. the mask flickered on every re-render (each Lab progress event while erasing).
 */
const MaskDrawerWrapper = forwardRef<{ handleMagicEraserGenerate: () => Promise<void> }, MaskDrawerWrapperProps>(({ onGenerate, isLabSdkEngine, isDisabled }, ref) => {
  const maskDrawerContext = useMaskDrawer()
  const { hasMaskDrawnRef, maskAnalyticsRef } = useClientPhotoStudio()
  // UnifiedPreview's memo comparator skips callback-only prop changes; read the latest handler at call time
  const onGenerateRef = useRef(onGenerate)
  onGenerateRef.current = onGenerate

  // Keep mask availability accessible outside of the drawer (for button enablement)
  useEffect(() => {
    if (!hasMaskDrawnRef) return
    hasMaskDrawnRef.current = maskDrawerContext?.hasMaskDrawn || null
    return () => {
      hasMaskDrawnRef.current = null
    }
  }, [hasMaskDrawnRef, maskDrawerContext])

  // Sync mask analytics data to context ref for VISUAL_GENERATED tracking
  useEffect(() => {
    if (!maskDrawerContext || !maskAnalyticsRef) return
    maskAnalyticsRef.current = {
      brushSize: maskDrawerContext.brushSize,
      numberOfMasksApplied: maskDrawerContext.numberOfMasksApplied,
    }
  }, [maskAnalyticsRef, maskDrawerContext])

  // Expose the generate function to parent component
  useImperativeHandle(ref, () => ({
    handleMagicEraserGenerate: async () => {
      try {
        if (!maskDrawerContext) {
          logger.error('MaskDrawer context not available', {
            context: {
              component: 'UnifiedPreview',
              action: 'generate'
            }
          })
          return
        }

        const { exportMaskAsFile } = maskDrawerContext
        // The Lab eraser reads a white-on-transparent mask; ai-visuals keeps its white-on-black one
        const maskedImageFile = await exportMaskAsFile(isLabSdkEngine ? { transparent: true } : undefined)
        if (maskedImageFile) {
          // Call the parent's generate function with the mask file
          onGenerateRef.current(maskedImageFile)
        } else if (isLabSdkEngine) {
          // Nothing marked: the controller explains, rather than a dead button
          onGenerateRef.current()
        }
      } catch (error) {
        logger.error('Error generating:', error, {
          context: {
            component: 'UnifiedPreview',
            action: 'generate'
          }
        })
      }
    }
  }), [isLabSdkEngine, maskDrawerContext])

  // Add null check to prevent context access errors
  if (!maskDrawerContext) {
    return null
  }

  return (
    <MaskDrawer isDisabled={isDisabled} />
  )
})

/** Lab SDK engine: the view controls' band above the photo, the mirror of the brush bar's below it */
const LAB_VIEW_CONTROLS_SX = { height: '5rem', marginTop: '2rem', marginBottom: '1.6rem', flexShrink: 0 }

const UnifiedPreviewComponent = forwardRef<UnifiedPreviewRef, UnifiedPreviewProps>(({
  imageUrl,
  previewImage,
  hasWorkspaceActiveSubscription,
  isProcessingImage,
  isSaving,
  currentFlow,
  selectedPinturaFeature,
  virtualStagingState,
  hasAIEraserCompleted,
  clearMaskTrigger,
  onMaskChange,
  onPreviewImageChange,
  onGenerate,
  generateRef,
  logPhotoStudioEvent,
  assignmentId,
  sourceVisualFilename,
}, ref) => {
  const { t } = useTranslation(['photo_studio_editing', 'common'])

  const {
    isVirtualStaging,
    isMagicEraserTool,
    hasMaskDrawnRef,
    maskAnalyticsRef,
    expandedVisualType,
    setExpandedVisualType,
    isOriginalVisualType,
    photoStudioHistory,
    aiProcessingStep,
    activeFeature,
    labProgress,
    labScene,
    showLabSceneImage,
    labStagePlan,
    labReveal,
    setLabReveal,
    isLabSdkEngine,
    labRunSources,
    isLabCompareAvailable,
    isLabCompare,
    setIsLabCompare,
    labEraserObjects,
    isLabBeforeView,
    labWorkingImage,
  } = useClientPhotoStudio()

  const isEnhanceTool = activeFeature === PhotoStudioSidebarOption.ENHANCE

  // The slider crops to its box (object-fit: cover), so it is sized to the photo's own aspect ratio
  const [previewAspectRatio, setPreviewAspectRatio] = useState<number | undefined>(undefined)
  // Lab SDK engine: Before | Compare | After once there is an earlier version (see `isLabCompareAvailable`), in every tool
  const showCompare = isLabCompareAvailable
  // While an order runs the slider hides: only the current image sits under the generating overlay (no seam)
  const isCompareView = showCompare && isLabCompare && !isProcessingImage

  /**
   * Lab SDK "Before" = the image the shown result was generated from (its recorded source), not always the
   * original — the same Before the Generate chevron offers. Undo/redo move it along; the very first iteration
   * falls back to the original. The ai-visuals engine keeps "Editing | Original", which shows the actual original.
   */
  const labBeforeImage = showCompare ? labRunSources.before : imageUrl
  const visualTypeSwitcherProps = {
    switchExpandedVisualType: (type: ExpandedVisualType) => {
      const wasShowingOriginal = expandedVisualType === ExpandedVisualType.ORIGINAL
      if (showCompare && type === ExpandedVisualType.EDITED) {
        // The eraser's After is the mask drawer, which has no reveal wipe
        showLabResult('after', wasShowingOriginal && !isMagicEraserTool)
        return
      }
      setIsLabCompare(false)
      setExpandedVisualType(type)
    },
    expandedVisualType,
    // Lab SDK engine: always the Before | Compare | After labels — disabled, not hidden, while nothing is to compare
    showCompare: isLabSdkEngine || showCompare,
    isCompareActive: isCompareView,
    onCompare: () => showLabResult('compare', expandedVisualType === ExpandedVisualType.ORIGINAL),
  }

  const clearLabReveal = useCallback(() => setLabReveal(null), [setLabReveal])

  // Frames the photo is contain-fitted into — the reveal wipe copies their geometry
  const stagingFrameRef = useRef<HTMLDivElement>(null)
  const enhanceFrameRef = useRef<HTMLDivElement>(null)
  const eraserFrameRef = useRef<HTMLDivElement>(null)
  // Lab SDK engine, eraser: Compare replaces the mask drawer; on Before the drawer draws on that Before (labWorkingImage),
  // which Generate then erases
  const isLabEraserAlternateView = isLabSdkEngine && isMagicEraserTool && isCompareView

  const { spawnErrorToast } = useSnackbar()

  /**
   * Show the Lab result (After or Compare) only once it is loaded and decoded — the slider would otherwise
   * paint black where the result belongs — and wipe the original away over it when coming from Before.
   */
  const showLabResult = useCallback(async (mode: 'after' | 'compare', animate: boolean) => {
    try {
      if (previewImage) await preloadImage(previewImage)
    } catch (error) {
      spawnErrorToast(t('photo_studio_editing:lab.error', { error: error instanceof Error ? error.message : String(error) }))
      return
    }
    setExpandedVisualType(ExpandedVisualType.EDITED)
    setIsLabCompare(mode === 'compare')
    if (animate && previewImage && previewImage !== labBeforeImage) setLabReveal({ from: labBeforeImage, to: previewImage })
  }, [labBeforeImage, previewImage, setExpandedVisualType, setIsLabCompare, setLabReveal, spawnErrorToast, t])

  /**
   * Lab SDK preview image with its Compare slider and result reveal — shared by the Enhance and
   * Staging (entire image) flows, which otherwise render their own <img>.
   */
  const renderLabPreviewImage = (imgRef: React.RefObject<HTMLImageElement>, frameRef: React.RefObject<HTMLDivElement>, alt: string) => (
    <>
      {isCompareView && (
        <Stack sx={{ width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
          <BeforeAfterSlider
            beforeImage={labBeforeImage}
            afterImage={previewImage}
            height="100%"
            handleAriaLabel={t('photo_studio_editing:enhance.before_after_slider_aria_label')}
            sx={{ aspectRatio: previewAspectRatio, width: previewAspectRatio ? 'auto' : '100%', maxWidth: '100%', maxHeight: '100%', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)' }}
          />
        </Stack>
      )}
      {!isCompareView && (
        <img
          ref={imgRef}
          src={displayedImage}
          alt={alt}
          onLoad={(event) => {
            const { naturalWidth, naturalHeight } = event.currentTarget
            if (naturalWidth && naturalHeight) setPreviewAspectRatio(naturalWidth / naturalHeight)
          }}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'center',
            display: 'block',
            margin: 'auto',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Lab SDK result reveal: wipes the previous image away from the top over the new one (or over the slider) */}
      {labReveal && displayedImage === labReveal.to && (
        <LabRevealOverlay key={`${labReveal.to}-${isCompareView}`} previousImageUrl={labReveal.from} frameRef={frameRef} aspectRatio={previewAspectRatio} onDone={clearLabReveal} />
      )}
      {/* Live AI label preview - burned into the image only on Save */}
      {expandedVisualType !== ExpandedVisualType.ORIGINAL && !isCompareView && (
        <AiLabelOverlay imgRef={imgRef} />
      )}
    </>
  )

  const { canUndo, canRedo } = photoStudioHistory.historyState
  // Lab SDK engine: a run is going from Generate until its result lands (the controller clears the progress then)
  const labRunProgress = isLabSdkEngine ? labProgress : null
  /**
   * The switch + undo/redo row above the preview. Lab SDK engine: both appear once there is an edit to undo or redo —
   * never Editing | Original — and stay; Before | Compare | After is disabled while there is no earlier version (e.g.
   * undone back to the first one). Its band mirrors the brush bar's below the photo (2rem · 5rem · 1.6rem), so the
   * photo sits centred and never moves when they appear. While a run goes, the run bar takes the band: the switch
   * cannot be used then, and the photo stays clear. The ai-visuals engine always shows both, in its original row.
   */
  const renderViewControls = (isSwitcherDisabled: boolean, isUndoRedoDisabled: boolean) => (
    <Stack gap=".8rem" flexDirection="row" justifyContent="center" alignItems="center" sx={isLabSdkEngine ? LAB_VIEW_CONTROLS_SX : undefined}>

      {labRunProgress && <LabRunBar progress={labRunProgress} plan={labStagePlan} />}

      {!labRunProgress && (!isLabSdkEngine || canUndo || canRedo) && (
        <VisualTypeSwitcher
          {...visualTypeSwitcherProps}
          disabled={isSwitcherDisabled || (isLabSdkEngine && !showCompare)}
        />
      )}

      {!labRunProgress && (!isLabSdkEngine || canUndo || canRedo) && (
        <SharedUndoRedoPanel
          logPhotoStudioEvent={logPhotoStudioEvent}
          assignmentId={assignmentId}
          sourceVisualFilename={sourceVisualFilename}
          disabled={isUndoRedoDisabled}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={photoStudioHistory.undo}
          onRedo={photoStudioHistory.redo}
          currentHistoryEntry={photoStudioHistory.historyState.current}
        />
      )}

    </Stack>
  )

  /**
   * Over the photo while a generation runs, clipped to it. Lab SDK engine: the photo stays clear (halo and scenes only;
   * the run bar is in the band above). The ai-visuals engine keeps its scrim, blur and rotating messages.
   */
  const renderGeneratingOverlay = (photoUrl: string, featureType: AIFeatureType) => isLabSdkEngine
    ? <LabLiveOverlay progress={labProgress} scene={labScene} previewImageUrl={photoUrl} onSceneImage={showLabSceneImage} />
    : (
      <PhotoStudioGenerating
        showText={true}
        showBlurOverlay={true}
        previewImageUrl={photoUrl}
        featureType={featureType}
        processingStep={aiProcessingStep}
        useDynamicMessages={true}
      />
    )

  // Refs for imperative handle
  const currentFlowRef = useRef(currentFlow)
  const previewImageRef = useRef(previewImage)

  // Refs to the preview <img> elements - anchor the live AI label overlay
  const stagingPreviewImgRef = useRef<HTMLImageElement>(null)
  const enhancePreviewImgRef = useRef<HTMLImageElement>(null)
  const eraserPreviewImgRef = useRef<HTMLImageElement>(null)

  // Update refs when props change
  currentFlowRef.current = currentFlow
  previewImageRef.current = previewImage

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    getCurrentFlow: () => currentFlowRef.current,
    getPreviewImage: () => previewImageRef.current,
  }), [])

  const shouldShowVisualStagingMaskingTools = isVirtualStaging && (
    virtualStagingState?.processingMode === ProcessingMode.PRESERVE_ZONE ||
    virtualStagingState?.processingMode === ProcessingMode.REPLACE_ZONE
  )

  const shouldShowBrushSizePanel = (isMagicEraserTool || shouldShowVisualStagingMaskingTools) && !selectedPinturaFeature

  // Get the appropriate tooltip for the generate button
  const getGenerateButtonTooltip = useCallback(() => {
    if (isVirtualStaging) {

      if (!virtualStagingState?.roomType) {
        return t('virtual_staging.room_type.room_type_required')
      }

      if (!virtualStagingState?.selectedStyle) {
        return t('virtual_staging.style_selector.style_required')
      }
    }

    if (isMagicEraserTool && !hasAIEraserCompleted) {
      return t('disabled_generate_button')
    }

    if (hasAIEraserCompleted) {
      return t('disabled_generate_no_new_mask')
    }

    return undefined
  }, [isVirtualStaging, virtualStagingState, isMagicEraserTool, hasAIEraserCompleted, t])

  const displayedImage = useMemo(() => {
    // On the Lab engine "Original" means the Before of the shown result (see labBeforeImage)
    return expandedVisualType === ExpandedVisualType.EDITED ? previewImage : labBeforeImage
  }, [expandedVisualType, labBeforeImage, previewImage])

  const renderPreviewContent = () => {
    // Magic Eraser flow - ONLY this flow has the eraser disclaimer
    if (isMagicEraserTool) {
      return (
        <>
          {renderViewControls(isProcessingImage || isSaving || !hasWorkspaceActiveSubscription, isProcessingImage || isSaving)}

          <MaskDrawerProvider
            key={`me-${labWorkingImage}`}
            imageUrl={labWorkingImage}
            currentPreviewImage={labWorkingImage}
            clearMaskTrigger={clearMaskTrigger}
            isMagicEraserTool={isMagicEraserTool}
            isVisualStaging={isVirtualStaging}
            onMaskChange={onMaskChange}
            // Clearing masks resets the preview to the drawer's image: never to a Before, which is not the current version
            onPreviewImageChange={isLabBeforeView ? undefined : onPreviewImageChange}
            logPhotoStudioEvent={logPhotoStudioEvent}
            assignmentId={assignmentId}
            sourceVisualFilename={sourceVisualFilename}
            isProcessingImage={isProcessingImage}
            isSaving={isSaving}
            labObjects={isLabSdkEngine ? labEraserObjects : undefined}
          >
            <>
              {/* Preview area that should shrink to fit available space. Lab SDK engine: Compare is drawn over the drawer,
                  which stays mounted (hidden) so a mask in progress survives the switch */}
              <Stack
                sx={{
                  flex: '1 1 0%',
                  minHeight: 0,
                  maxHeight: '100%',
                  overflow: 'hidden',
                  display: isLabEraserAlternateView ? 'none' : 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingLeft: '1.6rem',
                  paddingRight: '1.6rem',
                }}
              >
                <MaskDrawerWrapper
                  ref={generateRef}
                  onGenerate={onGenerate}
                  isLabSdkEngine={isLabSdkEngine}
                  isDisabled={!hasWorkspaceActiveSubscription || (isOriginalVisualType && !isLabSdkEngine)}
                />
              </Stack>

              {isLabEraserAlternateView && (
                <Stack
                  sx={{
                    flex: '1 1 0%',
                    minHeight: 0,
                    maxHeight: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingLeft: '1.6rem',
                    paddingRight: '1.6rem',
                  }}
                >
                  <div ref={eraserFrameRef} style={{ position: 'relative', display: 'inline-block', width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', overflow: 'hidden' }}>
                    {renderLabPreviewImage(eraserPreviewImgRef, eraserFrameRef, 'eraser_preview')}
                  </div>
                </Stack>
              )}

              {/* Brush size panel - fixed size, no shrink; keeps its place (hidden) while Compare shows */}
              {shouldShowBrushSizePanel && (
                <Stack sx={{ flexShrink: 0, marginTop: '1.6rem', marginBottom: '2rem', alignItems: 'center', visibility: isLabEraserAlternateView ? 'hidden' : 'visible' }}>
                  <Stack
                    sx={{
                      backgroundColor: GRAY_1100,
                      padding: '0.8rem',
                      borderRadius: '4rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 'fit-content',
                    }}
                  >
                    <PhotoStudioPreviewToolbar />
                  </Stack>
                </Stack>
              )}

            </>
          </MaskDrawerProvider>

        </>
      )
    }

    // Virtual/Visual Staging - Apply to entire image flow (NO masking, just generate button)
    if (isVirtualStaging && !shouldShowVisualStagingMaskingTools) {
      return (
        <>

          {renderViewControls(isProcessingImage || isSaving || !hasWorkspaceActiveSubscription, isProcessingImage || isSaving)}

          {/* Preview area that should shrink to fit available space */}
          <Stack
            sx={{
              flex: '1 1 0%',
              minHeight: 0,
              maxHeight: '100%',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingLeft: '1.6rem',
              paddingRight: '1.6rem',
            }}
          >
            <div
              ref={stagingFrameRef}
              style={{
                position: 'relative',
                display: 'inline-block',
                width: '100%',
                height: '100%',
                maxWidth: '100%',
                maxHeight: '100%',
                overflow: 'hidden', // Clip PhotoStudioLoading to image container boundaries
              }}
            >
              {renderLabPreviewImage(stagingPreviewImgRef, stagingFrameRef, 'virtual_staging_preview')}
              {/* Loading animation overlay - clipped to image boundaries */}
              {(isProcessingImage || virtualStagingState?.isGeneratingStaging) && renderGeneratingOverlay(previewImage, getAIFeatureType(virtualStagingState?.generationType))}
            </div>
          </Stack>

          {/* Spacer to match Brush size panel height for consistent preview positioning */}
          <Stack sx={{ flexShrink: 0, marginTop: '1.6rem', marginBottom: '2rem', alignItems: 'center' }}>
            <Stack sx={{ height: '5rem', width: '1px' }} /> {/* Invisible spacer matching toolbar height */}
          </Stack>

        </>
      )
    }

    // Visual Staging - Preserve/Replace flows (has masking and brush tools, NO eraser disclaimer)
    if (shouldShowVisualStagingMaskingTools) {
      return (
        <>

          {renderViewControls(isProcessingImage || isSaving || !hasWorkspaceActiveSubscription, isProcessingImage || isSaving || isOriginalVisualType)}

          <MaskDrawerProvider
            key={`vs-mask-${previewImage}`}
            imageUrl={previewImage}
            currentPreviewImage={displayedImage}
            clearMaskTrigger={clearMaskTrigger}
            isMagicEraserTool={false}
            isVisualStaging={isVirtualStaging}
            onMaskChange={onMaskChange}
            onPreviewImageChange={onPreviewImageChange}
            logPhotoStudioEvent={logPhotoStudioEvent}
            assignmentId={assignmentId}
            sourceVisualFilename={sourceVisualFilename}
          >
            <>
              {/* Preview area that should shrink to fit available space */}
              <Stack
                sx={{
                  flex: '1 1 0%',
                  minHeight: 0,
                  maxHeight: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingLeft: '1.6rem',
                  paddingRight: '1.6rem',
                }}
              >
                <MaskDrawerWrapper
                  ref={generateRef}
                  onGenerate={onGenerate}
                  isLabSdkEngine={isLabSdkEngine}
                  isDisabled={!hasWorkspaceActiveSubscription || isOriginalVisualType}
                />
              </Stack>

              {/* Brush size panel - fixed size, no shrink */}
              {shouldShowBrushSizePanel && (
                <Stack sx={{ flexShrink: 0, marginTop: '1.6rem', marginBottom: '2rem', alignItems: 'center' }}>
                  <Stack
                    sx={{
                      backgroundColor: GRAY_1100,
                      padding: '0.8rem',
                      borderRadius: '4rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 'fit-content',
                    }}
                  >
                    <PhotoStudioPreviewToolbar />
                  </Stack>
                </Stack>
              )}

              {isSaving && (
                <PhotoStudioSaving
                  showBlurOverlay={true}
                  previewImageUrl={displayedImage}
                />
              )}
              {(isProcessingImage || virtualStagingState?.isGeneratingStaging) && !isSaving && renderGeneratingOverlay(displayedImage, getAIFeatureType(virtualStagingState?.generationType))}
            </>
          </MaskDrawerProvider>
        </>
      )
    }

    // AI Enhancement flow - preview with Edited/Original toggle and Undo/Redo
    if (isEnhanceTool) {
      // Non-subscriber: show before/after demo slider - we use this slider only for non-subscribers for Enhance feature
      if (!hasWorkspaceActiveSubscription) {
        return (
          <Stack
            sx={{
              flex: '1 1 0%',
              minHeight: 0,
              maxHeight: '100%',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1.6rem',
              pointerEvents: 'auto',
            }}
          >
            <BeforeAfterSlider
              beforeImage={enhanceDemoBefore}
              afterImage={enhanceDemoAfter}
              height="auto"
              handleAriaLabel={t('photo_studio_editing:enhance.before_after_slider_aria_label')}
              sx={{
                maxWidth: '80%',
                maxHeight: '80%',
                aspectRatio: '16 / 10',
              }}
            />
          </Stack>
        )
      }

      return (
        <>
          {renderViewControls(isProcessingImage || isSaving, isProcessingImage || isSaving)}

          <Stack
            sx={{
              flex: '1 1 0%',
              minHeight: 0,
              maxHeight: '100%',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingLeft: '1.6rem',
              paddingRight: '1.6rem',
            }}
          >
            <div
              ref={enhanceFrameRef}
              style={{
                position: 'relative',
                display: 'inline-block',
                width: '100%',
                height: '100%',
                maxWidth: '100%',
                maxHeight: '100%',
                overflow: 'hidden',
              }}
            >
              {renderLabPreviewImage(enhancePreviewImgRef, enhanceFrameRef, 'enhancement_preview')}
              {isProcessingImage && renderGeneratingOverlay(previewImage, getAIFeatureType(undefined, false, true))}
            </div>
          </Stack>

          {/* Spacer to match Brush size panel height for consistent preview positioning */}
          <Stack sx={{ flexShrink: 0, marginTop: '1.6rem', marginBottom: '2rem', alignItems: 'center' }}>
            <Stack sx={{ height: '5rem', width: '1px' }} /> {/* Invisible spacer matching toolbar height */}
          </Stack>
        </>
      )
    }

    // Default image preview (for non-AI tools)
    return (
      <img
        src={displayedImage}
        alt="loaded_visual_for_edit_non_ai_preview"
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
          objectPosition: 'center',
          display: 'block',
          margin: 'auto',
        }}
      />
    )
  }

  return (
    <PreviewContainer
      hasWorkspaceActiveSubscription={hasWorkspaceActiveSubscription}
    >
      {renderPreviewContent()}
    </PreviewContainer>
  )
})

// Memoize to prevent re-renders when only settings change (disclaimer, processingMode, etc.)
// but still allow re-renders when preview image or critical props change
export const UnifiedPreview = memo(UnifiedPreviewComponent, (prevProps: UnifiedPreviewProps, nextProps: UnifiedPreviewProps) => {
  // Re-render if preview image changes
  if (prevProps.previewImage !== nextProps.previewImage) return false
  if (prevProps.imageUrl !== nextProps.imageUrl) return false

  // Re-render if processing state changes
  if (prevProps.isProcessingImage !== nextProps.isProcessingImage) return false
  if (prevProps.isSaving !== nextProps.isSaving) return false

  // Re-render if other critical props change
  if (prevProps.hasWorkspaceActiveSubscription !== nextProps.hasWorkspaceActiveSubscription) return false
  if (prevProps.clearMaskTrigger !== nextProps.clearMaskTrigger) return false
  if (prevProps.hasAIEraserCompleted !== nextProps.hasAIEraserCompleted) return false
  if (prevProps.selectedPinturaFeature !== nextProps.selectedPinturaFeature) return false

  // Check if we're switching between fundamentally different flow types
  const prevIsPintura = prevProps.currentFlow === PhotoStudioFlowType.VISUAL_EDITING_PINTURA
  const nextIsPintura = nextProps.currentFlow === PhotoStudioFlowType.VISUAL_EDITING_PINTURA
  const prevIsMagicEraser = prevProps.currentFlow === PhotoStudioFlowType.VISUAL_EDITING_MAGIC_ERASER
  const nextIsMagicEraser = nextProps.currentFlow === PhotoStudioFlowType.VISUAL_EDITING_MAGIC_ERASER
  const prevIsVisualStaging = prevProps.currentFlow?.toString().startsWith('VISUAL_STAGING') ?? false
  const nextIsVisualStaging = nextProps.currentFlow?.toString().startsWith('VISUAL_STAGING') ?? false

  // Re-render if switching between Pintura and AI flows
  if (prevIsPintura !== nextIsPintura) return false
  if (prevIsMagicEraser !== nextIsMagicEraser) return false
  if (prevIsVisualStaging !== nextIsVisualStaging) return false

  // Check if isMagicEraserTool prop changed
  if (prevProps.isMagicEraserTool !== nextProps.isMagicEraserTool) {
    return false // Re-render - different mask drawer type
  }

  // Check if processingMode changed - this affects which UI branch is rendered
  // We need to re-render to show/hide mask tools, but we'll prevent the preview image from blinking
  // by ensuring the animation key doesn't change when only processingMode changes
  const prevProcessingMode = prevProps.virtualStagingState?.processingMode
  const nextProcessingMode = nextProps.virtualStagingState?.processingMode
  if (prevProcessingMode !== nextProcessingMode) {
    return false // Re-render - mask tools need to show/hide (but animation key won't change)
  }

  // Don't re-render for virtualStagingState changes that don't affect the preview image
  // (roomType, style, generationType, keepFloorUntouched, wallColor, floorTemplate, maskData)
  // These are just settings that don't affect what's rendered

  // Don't re-render for currentFlow changes when staying within Visual Staging subtypes
  // (ENTIRE_IMAGE, PRESERVE_ZONE, REPLACE_ZONE) - these are just processingMode changes
  // which we already handled above

  // Don't re-render for other prop changes (callbacks, analytics props, etc.)
  return true // Skip re-render
})
